import { ReactNode } from 'react';

import { Text, VSpacing } from '@hh.ru/magritte-ui';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import FormatMoney from 'src/components/FormatMoney';
import PriceCartExpBanner from 'src/components/Price/PriceCart/PriceCartExpBanner';
import translation from 'src/components/translation';
import { useSelectorNonNullable } from 'src/hooks/useSelector';
import { NON_BREAKING_SPACE } from 'src/utils/constants/symbols';

import PriceCartDiscount from 'src/components/Price/PriceCart/PriceCartFooter/PriceCartDiscount';
import PriceCartPayButton from 'src/components/Price/PriceCart/PriceCartFooter/PriceCartPayButton';
import PriceChangesBanner from 'src/components/Price/PriceCart/PriceCartFooter/PriceChangesBanner';
import usePriceChangeNotificationCartItem from 'src/components/Price/PriceCart/PriceCartFooter/PriceChangesBanner/hooks/usePriceChangeNotificationCartItem';
import TwoColumnsWrapper from 'src/components/Price/PriceCart/PriceCartFooter/TwoColumnsWrapper';

const TrlKeys = {
    scaledPrice: 'employer.price.cart.scaledPrice',
    total: 'employer.price.cart.totalPrice',
    totalProfit: 'employer.price.cart.profit',
};

const initEventListener = (element: HTMLSpanElement) => {
    if (element) {
        element.addEventListener('animationend', () => {
            element.classList.remove('price-cart-update-blink');
        });
    }
};

const renderTotalCost = (amount: string, currencyNode: ReactNode) => (
    <>
        <span className="HH-AddItemToCartAnimation-TotalCost" data-qa="money__amount" ref={initEventListener}>
            {amount}
        </span>
        {NON_BREAKING_SPACE}
        {currencyNode}
    </>
);

const PriceCartFooter: TranslatedComponent = ({ trls }) => {
    const { currency, totalPrice, scaledPrice, totalProfit } = useSelectorNonNullable(({ priceCart }) => priceCart);
    const showDiscountEditor = useSelectorNonNullable(({ priceData }) => priceData.showDiscountEditor);
    const priceChangeNotification = usePriceChangeNotificationCartItem();

    const showDiscount = !!totalProfit;

    return (
        <>
            {showDiscountEditor && <PriceCartDiscount />}
            {showDiscount && scaledPrice && (
                <>
                    <TwoColumnsWrapper>
                        <Text Element="span" typography="label-3-regular" style="secondary">
                            {trls[TrlKeys.scaledPrice]}
                        </Text>
                        <Text Element="span" typography="label-3-regular">
                            <FormatMoney currency={currency} inCents={true}>
                                {scaledPrice}
                            </FormatMoney>
                        </Text>
                    </TwoColumnsWrapper>
                </>
            )}
            {showDiscount && (
                <>
                    <VSpacing default={4} />
                    <TwoColumnsWrapper>
                        <Text Element="span" typography="label-3-regular" style="secondary">
                            {trls[TrlKeys.totalProfit]}
                        </Text>
                        <Text Element="span" typography="label-3-regular">
                            <FormatMoney currency={currency} inCents={true}>
                                {totalProfit}
                            </FormatMoney>
                        </Text>
                    </TwoColumnsWrapper>
                    <VSpacing default={4} />
                </>
            )}
            <TwoColumnsWrapper>
                <Text Element="span" typography="subtitle-1-semibold">
                    {trls[TrlKeys.total]}
                </Text>
                <Text Element="span" typography="subtitle-1-semibold" data-qa="cart__total-cost">
                    <FormatMoney currency={currency} inCents={true} render={renderTotalCost}>
                        {totalPrice}
                    </FormatMoney>
                </Text>
            </TwoColumnsWrapper>
            <VSpacing default={24} />
            {/* tempexp_36140_next-line */}
            <PriceCartExpBanner />
            {!!priceChangeNotification && (
                <>
                    <PriceChangesBanner notification={priceChangeNotification} />
                    <VSpacing default={12} />
                </>
            )}
            <PriceCartPayButton />
        </>
    );
};

export default translation(PriceCartFooter);
